'use client';
import MuiBox from '@new-components/atoms/MuiBox';
import Image from '@new-components/atoms/Image';
import VideoPlayer from '@new-components/atoms/VideoPlayer';
import MuiModal from '@new-components/atoms/MuiModal';
import {IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import React, { useEffect } from 'react';
import {ImgProps} from '@custom-types/ImgProps';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

export type Media = {videoURL: string; image: ImgProps};
export type CustomVideoPlayerProps = {
  media: Media;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
};

export const CustomDesktopVideoPlayer = (props: CustomVideoPlayerProps) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const handleFullscreenToggle = () => {
    const cardElement = document.getElementById('new-launch-card');
    if (!cardElement) return;
    if (isFullscreen) {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    } else {
      // Enter fullscreen
      if (cardElement.requestFullscreen) {
        cardElement.requestFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
    if(props.onFullscreenToggle) props.onFullscreenToggle(!isFullscreen);
  };
  return (
    <>
      <Image
        src={props.media.image.src}
        alt={props.media.image.alt}
        fill
        style={{objectFit: 'cover', position: 'absolute', top: 0, left: 0}}
      />
      <MuiBox
        sx={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          ['video']: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          },
        }}>
        <IconButton
          onClick={handleFullscreenToggle}
          sx={{
            'position': 'absolute',
            'bottom': 16,
            'right': 16,
            'backgroundColor': 'rgba(0, 0, 0, 0.5)',
            'color': 'white',
            'zIndex': 11,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          }}>
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
        <VideoPlayer
          className='react-player'
          height={'100%'}
          width={'100%'}
          url={props.media.videoURL}
          playing
          muted={!isFullscreen}
          loop
          playsinline
          controls={isFullscreen}
        />
      </MuiBox>
    </>
  );
};

export const CustomMobVideoPlayer = (props: CustomVideoPlayerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  useEffect(()=>{
    if(props.onFullscreenToggle){
      props.onFullscreenToggle(isOpen);
    }
  },[isOpen])

  return (
    <>
      <Image
        src={props.media.image.src}
        alt={props.media.image.alt}
        fill
        style={{objectFit: 'cover', position: 'absolute', top: 0, left: 0}}
      />
      <IconButton
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
        }}
        size='large'
        onClick={() => {
          setIsOpen(true);
        }}>
        <PlayCircleOutlineIcon sx={{fontSize: 40}} />
      </IconButton>
      <MuiModal open={isOpen}>
        <MuiBox
          sx={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            zIndex: 10,
            background: 'black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <MuiBox
            component={'span'}
            tabIndex={0}
            onClick={() => setIsOpen(false)}
            sx={{
              display: 'flex',
              fontWeight: 600,
              color: 'white',
              cursor: 'pointer',
              width: '96%',
            }}>
            <CloseIcon />
            Close
          </MuiBox>
          <VideoPlayer
            url={props.media.videoURL}
            width='96%'
            height='80%'
            muted={!isOpen}
            playing={isOpen}
            playsinline
            controls={true}
          />
        </MuiBox>
      </MuiModal>
    </>
  );
};
